import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Order, Product } from "@shared/types";
import { api } from "@/lib/api";
import { AdminLayout } from "./AdminLayout";
import { formatMoney } from "@/lib/format";

export function AdminDashboard() {
  const [products, setProducts] = useState<Product[] | null>(null);
  const [orders, setOrders] = useState<Order[] | null>(null);

  useEffect(() => {
    api.admin.products.list().then(setProducts).catch(() => setProducts([]));
    api.admin.orders.list().then(setOrders).catch(() => setOrders([]));
  }, []);

  const pendingOrders = orders?.filter((o) => o.status === "pending") ?? [];
  const awaitingPayment = orders?.filter((o) => o.paymentStatus === "unpaid") ?? [];
  const lowStock = products?.filter((p) => p.isActive && p.stockQty <= 3) ?? [];

  return (
    <AdminLayout>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Stat label="Products" value={products?.length ?? "…"} />
        <Stat label="Pending orders" value={pendingOrders.length} />
        <Stat label="Awaiting payment" value={awaitingPayment.length} />
        <Stat label="Low stock" value={lowStock.length} />
      </div>

      <div className="mt-8 grid gap-6 sm:grid-cols-2">
        <div className="rounded-xl border border-stone-300 bg-white p-5">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="font-display text-base font-medium text-ink">Recent orders</h2>
            <Link to="/admin/orders" className="text-xs font-semibold text-stone-700">
              View all
            </Link>
          </div>
          <div className="flex flex-col divide-y divide-stone-100">
            {(orders ?? []).slice(0, 5).map((o) => (
              <Link key={o.id} to={`/admin/orders/${o.id}`} className="flex items-center justify-between py-2.5 text-sm">
                <div>
                  <p className="font-semibold text-ink">{o.orderNumber}</p>
                  <p className="text-xs text-stone-500">{o.customerName}</p>
                </div>
                <span className="text-sm text-ink">{formatMoney(o.subtotalCents, o.currency)}</span>
              </Link>
            ))}
            {orders?.length === 0 && <p className="py-4 text-sm text-stone-500">No orders yet.</p>}
          </div>
        </div>

        <div className="rounded-xl border border-stone-300 bg-white p-5">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="font-display text-base font-medium text-ink">Low stock</h2>
            <Link to="/admin/products" className="text-xs font-semibold text-stone-700">
              Manage
            </Link>
          </div>
          <div className="flex flex-col divide-y divide-stone-100">
            {lowStock.slice(0, 5).map((p) => (
              <Link key={p.id} to={`/admin/products/${p.id}`} className="flex items-center justify-between py-2.5 text-sm">
                <span className="text-ink">{p.name}</span>
                <span className="text-xs font-semibold text-wine">{p.stockQty} left</span>
              </Link>
            ))}
            {lowStock.length === 0 && <p className="py-4 text-sm text-stone-500">Stock levels look healthy.</p>}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-xl border border-stone-300 bg-white p-5">
      <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">{label}</p>
      <p className="mt-1 font-display text-2xl font-medium text-ink">{value}</p>
    </div>
  );
}
